"use client";

import { EditorContent, useEditor } from "@tiptap/react";
import {
	Bold,
	Code,
	Heading2,
	Image as ImageIcon,
	Italic,
	Link as LinkIcon,
	List,
	ListOrdered,
	Loader2,
	Palette,
	Paperclip,
	Redo2,
	RotateCcw,
	Send,
	Smile,
	Strikethrough,
	Undo2,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useStore } from "zustand";
import { defaultGifs } from "@/lib/default-gifs";
import { uiStore } from "@/lib/stores/ui-store";
import {
	convertToDirectImageUrl,
	getTiptapExtensions,
	IMAGE_EMBEDDING_MAX_WIDTH,
	type MentionRole,
	type MentionUser,
	tiptapEditorStyles,
} from "@/lib/tiptap-config";
import { cn } from "@/lib/utils";
import { Button } from "./button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from "./dialog";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuRadioGroup,
	DropdownMenuRadioItem,
	DropdownMenuTrigger,
} from "./dropdown-menu";
import { EmojiPicker } from "./emoji-picker";
import { type GifItem, GifPicker } from "./gif-picker";
import { Input } from "./input";
import { Kbd } from "./kbd";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from "./tooltip";

type TiptapEditorProps = {
	onSubmit?: (content: string, json: Record<string, unknown>) => void;
	placeholder?: string;
	className?: string;
	editorClassName?: string;
	disabled?: boolean;
	gifs?: GifItem[];
	users?: Omit<MentionUser, "type">[];
	roles?: Omit<MentionRole, "type">[];
	onAttachClick?: () => void;
	isUploading?: boolean;
	initialContent?: Record<string, unknown>;
	onContentChange?: (json: Record<string, unknown>) => void;
	onCancel?: () => void;
	submitLabel?: string;
	autoFocus?: boolean;
	onTyping?: () => void;
};

export function TiptapEditor({
	placeholder = "Write something...",
	className,
	editorClassName,
	disabled = false,
	onSubmit,
	gifs = defaultGifs,
	users = [],
	roles = [],
	onAttachClick,
	isUploading = false,
	initialContent,
	onContentChange,
	onCancel,
	submitLabel = "Send message",
	autoFocus = false,
	onTyping,
}: TiptapEditorProps) {
	const [linkDialogOpen, setLinkDialogOpen] = useState(false);
	const [linkUrl, setLinkUrl] = useState("");
	const [imageDialogOpen, setImageDialogOpen] = useState(false);
	const [imageUrl, setImageUrl] = useState("");
	const [imageAlt, setImageAlt] = useState("");
	const [emojiPickerOpen, setEmojiPickerOpen] = useState(false);
	const [gifPickerOpen, setGifPickerOpen] = useState(false);
	const [colorPickerOpen, setColorPickerOpen] = useState(false);

	const isEmptyNode = (node: Record<string, unknown>): boolean => {
		if (
			node.text &&
			typeof node.text === "string" &&
			node.text.trim().length > 0
		) {
			return false;
		}

		const nodeType = node.type as string;

		if (nodeType === "image" || nodeType === "emoji" || nodeType === "mention")
			return false;

		if (nodeType === "hardBreak") return true;

		if (
			!node.content ||
			(Array.isArray(node.content) && node.content.length === 0)
		) {
			return true;
		}
		if (Array.isArray(node.content)) {
			return node.content.every((child) =>
				isEmptyNode(child as Record<string, unknown>)
			);
		}
		return false;
	};

	const trimEmptyNodes = (node: Record<string, unknown>): void => {
		if (node.content && Array.isArray(node.content)) {
			while (
				node.content.length > 0 &&
				isEmptyNode(node.content[0] as Record<string, unknown>)
			) {
				node.content.shift();
			}
			while (
				node.content.length > 0 &&
				isEmptyNode(node.content.at(-1) as Record<string, unknown>)
			) {
				node.content.pop();
			}
			for (const child of node.content) {
				trimEmptyNodes(child as Record<string, unknown>);
			}
		}
	};

	const handleSubmit = () => {
		if (onSubmit && editorInstance) {
			const json = editorInstance.getJSON();
			trimEmptyNodes(json);

			if (isEmptyNode(json)) {
				return;
			}

			const html = editorInstance.getHTML();
			onSubmit(html, json);
			editorInstance.commands.clearContent();
		}
	};

	const onContentChangeRef = useRef(onContentChange);
	onContentChangeRef.current = onContentChange;

	const usersRef = useRef(users);
	const rolesRef = useRef(roles);
	usersRef.current = users;
	rolesRef.current = roles;

	const getMentionItems = useCallback(
		() => ({ users: usersRef.current, roles: rolesRef.current }),
		[]
	);

	const [extensions] = useState(() =>
		getTiptapExtensions(placeholder, getMentionItems)
	);

	const editorInstance = useEditor({
		extensions,
		editable: !disabled,
		content: initialContent,
		onUpdate: ({ editor }) => {
			onContentChangeRef.current?.(editor.getJSON());
			onTyping?.();
		},
	});

	useEffect(() => {
		if (autoFocus && editorInstance) {
			editorInstance.commands.focus("end");
		}
	}, [autoFocus, editorInstance]);

	const sendKeyMode = useStore(uiStore, (state) => state.sendKeyMode);

	useEffect(() => {
		if (!editorInstance) return;

		const handleKeyDown = (e: KeyboardEvent) => {
			if (sendKeyMode === "cmd-enter") {
				if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
					e.preventDefault();
					handleSubmit();
				}
			} else if (e.key === "Enter" && !e.shiftKey && !e.metaKey && !e.ctrlKey) {
				e.preventDefault();
				handleSubmit();
			}
			if (e.key === "Escape" && onCancel) {
				e.preventDefault();
				onCancel();
			}
		};

		const editorElement = editorInstance.view.dom;
		editorElement.addEventListener("keydown", handleKeyDown);

		return () => {
			editorElement.removeEventListener("keydown", handleKeyDown);
		};
	}, [editorInstance, onSubmit, sendKeyMode]);

	if (!editorInstance) {
		return null;
	}

	const toggleBold = () => editorInstance.chain().focus().toggleBold().run();
	const toggleItalic = () =>
		editorInstance.chain().focus().toggleItalic().run();
	const toggleStrike = () =>
		editorInstance.chain().focus().toggleStrike().run();
	const toggleCode = () => editorInstance.chain().focus().toggleCode().run();
	const toggleCodeBlock = () =>
		editorInstance.chain().focus().toggleCodeBlock().run();
	const toggleBulletList = () =>
		editorInstance.chain().focus().toggleBulletList().run();
	const toggleOrderedList = () =>
		editorInstance.chain().focus().toggleOrderedList().run();
	const setHeading = (level: 1 | 2 | 3 | 4 | 5 | 6) => {
		editorInstance.chain().focus().toggleHeading({ level }).run();
	};

	const getCurrentHeadingLevel = (): string => {
		const MAX_HEADING_LEVEL = 6;
		const MIN_HEADING_LEVEL = 1;
		for (let i = MIN_HEADING_LEVEL; i <= MAX_HEADING_LEVEL; i++) {
			if (
				editorInstance.isActive("heading", {
					level: i as 1 | 2 | 3 | 4 | 5 | 6,
				})
			) {
				return String(i);
			}
		}
		return "";
	};

	const handleLinkSubmit = () => {
		if (linkUrl) {
			editorInstance
				.chain()
				.focus()
				.extendMarkRange("link")
				.setLink({ href: linkUrl })
				.run();
			setLinkUrl("");
			setLinkDialogOpen(false);
		}
	};

	const handleImageSubmit = () => {
		if (imageUrl) {
			// Convert share URLs to direct media URLs
			const directUrl = convertToDirectImageUrl(imageUrl);
			editorInstance
				.chain()
				.focus()
				.setImage({
					src: directUrl,
					alt: imageAlt || "Image",
					width: IMAGE_EMBEDDING_MAX_WIDTH,
				})
				.run();
			setImageUrl("");
			setImageAlt("");
			setImageDialogOpen(false);
		}
	};

	const undo = () => editorInstance.chain().focus().undo().run();
	const redo = () => editorInstance.chain().focus().redo().run();

	const textColors = [
		{ name: "Cyan", value: "#00a6ce" },
		{ name: "Light Cyan", value: "#b8dce1" },
		{ name: "Teal", value: "#00657f" },
		{ name: "Grey", value: "#949493" },
		{ name: "Yellow", value: "#ffc600" },
		{ name: "Light Yellow", value: "#f8e0a4" },
		{ name: "Gold", value: "#d39e0a" },
		{ name: "Orange", value: "#ff661b" },
		{ name: "Peach", value: "#ffc999" },
		{ name: "Rust", value: "#c3502f" },
		{ name: "Navy", value: "#00416a" },
		{ name: "Sky Blue", value: "#bad8ea" },
		{ name: "Green", value: "#46a040" },
		{ name: "Mint", value: "#a0d8b3" },
		{ name: "Forest", value: "#286040" },
		{ name: "Red", value: "#f42434" },
		{ name: "Pink", value: "#ffb1b8" },
		{ name: "Maroon", value: "#802528" },
		{ name: "Purple", value: "#77216c" },
		{ name: "Lavender", value: "#cfacce" },
		{ name: "Plum", value: "#54274e" },
	];

	const setTextColor = (color: string) => {
		if (color === "") {
			editorInstance.chain().focus().unsetColor().run();
		} else {
			editorInstance.chain().focus().setColor(color).run();
		}
		setColorPickerOpen(false);
	};

	const resetTextColor = () => {
		editorInstance.chain().focus().unsetColor().run();
		setColorPickerOpen(false);
	};

	const getCurrentTextColor = (): string => {
		const colorAttr = editorInstance.getAttributes("textStyle").color;
		return typeof colorAttr === "string" ? colorAttr : "";
	};

	return (
		<div className={cn("bg-background", className)}>
			<TooltipProvider delayDuration={500}>
				<div className="flex flex-wrap items-center gap-1 border-b bg-muted/30 px-2">
					<Tooltip>
						<TooltipTrigger asChild>
							<Button
								className={cn(
									"size-8",
									editorInstance.isActive("bold") &&
										"bg-primary text-primary-foreground"
								)}
								disabled={disabled}
								onClick={toggleBold}
								size="icon"
								variant="ghost"
							>
								<Bold className="size-4" />
							</Button>
						</TooltipTrigger>
						<TooltipContent>
							<div className="flex items-center gap-1">
								<span>Bold</span>
								<Kbd>Ctrl</Kbd>
								<Kbd>B</Kbd>
							</div>
						</TooltipContent>
					</Tooltip>

					<Tooltip>
						<TooltipTrigger asChild>
							<Button
								className={cn(
									"size-8",
									editorInstance.isActive("italic") &&
										"bg-primary text-primary-foreground"
								)}
								disabled={disabled}
								onClick={toggleItalic}
								size="icon"
								variant="ghost"
							>
								<Italic className="size-4" />
							</Button>
						</TooltipTrigger>
						<TooltipContent>
							<div className="flex items-center gap-1">
								<span>Italic</span>
								<Kbd>Ctrl</Kbd>
								<Kbd>I</Kbd>
							</div>
						</TooltipContent>
					</Tooltip>

					<Tooltip>
						<TooltipTrigger asChild>
							<Button
								className={cn(
									"size-8",
									editorInstance.isActive("strike") &&
										"bg-primary text-primary-foreground"
								)}
								disabled={disabled}
								onClick={toggleStrike}
								size="icon"
								variant="ghost"
							>
								<Strikethrough className="size-4" />
							</Button>
						</TooltipTrigger>
						<TooltipContent>
							<div className="flex items-center gap-1">
								<span>Strikethrough</span>
								<Kbd>Ctrl</Kbd>
								<Kbd>Shift</Kbd>
								<Kbd>X</Kbd>
							</div>
						</TooltipContent>
					</Tooltip>

					<Tooltip>
						<Popover onOpenChange={setColorPickerOpen} open={colorPickerOpen}>
							<PopoverTrigger asChild>
								<TooltipTrigger asChild>
									<Button
										className="size-8"
										disabled={disabled}
										size="icon"
										variant="ghost"
									>
										<Palette className="size-4" />
									</Button>
								</TooltipTrigger>
							</PopoverTrigger>
							<PopoverContent className="w-auto p-3">
								<div className="mb-2 flex items-center justify-between gap-2">
									<div className="flex items-center gap-2">
										<span className="text-muted-foreground text-xs">
											Current:
										</span>
										{getCurrentTextColor() ? (
											<span
												className="inline-block size-5 rounded border border-border"
												style={{ backgroundColor: getCurrentTextColor() }}
												title={
													textColors.find(
														(c) => c.value === getCurrentTextColor()
													)?.name || getCurrentTextColor()
												}
											/>
										) : (
											<span className="text-muted-foreground text-xs">
												Default
											</span>
										)}
									</div>
									<Button
										aria-label="Reset to default color"
										className="h-6 gap-1 px-2 text-xs"
										disabled={!getCurrentTextColor()}
										onClick={resetTextColor}
										size="sm"
										variant="outline"
									>
										<RotateCcw className="size-3" />
										Reset
									</Button>
								</div>
								<div className="grid grid-cols-6 gap-1">
									{textColors.map((color) => (
										<button
											aria-label={`Set text color to ${color.name}`}
											className={cn(
												"size-6 rounded-md border transition-transform hover:scale-110 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1",
												getCurrentTextColor() === color.value
													? "border-foreground ring-2 ring-foreground ring-offset-1"
													: "border-border"
											)}
											key={color.name}
											onClick={() => setTextColor(color.value)}
											style={{ backgroundColor: color.value }}
											title={color.name}
											type="button"
										/>
									))}
								</div>
							</PopoverContent>
						</Popover>
						<TooltipContent>
							<span>Text Color</span>
						</TooltipContent>
					</Tooltip>

					<div className="mx-1 h-4 w-px bg-border" />

					<Tooltip>
						<DropdownMenu>
							<DropdownMenuTrigger asChild>
								<TooltipTrigger asChild>
									<Button
										className={cn(
											"size-8",
											getCurrentHeadingLevel() !== "" &&
												"bg-primary text-primary-foreground"
										)}
										disabled={disabled}
										size="icon"
										variant="ghost"
									>
										<Heading2 className="size-4" />
									</Button>
								</TooltipTrigger>
							</DropdownMenuTrigger>
							<DropdownMenuContent align="start">
								<DropdownMenuRadioGroup
									onValueChange={(value) => {
										const level = Number.parseInt(value, 10) as
											| 1
											| 2
											| 3
											| 4
											| 5
											| 6;
										setHeading(level);
									}}
									value={getCurrentHeadingLevel()}
								>
									<DropdownMenuRadioItem value="1">
										Heading 1
									</DropdownMenuRadioItem>
									<DropdownMenuRadioItem value="2">
										Heading 2
									</DropdownMenuRadioItem>
									<DropdownMenuRadioItem value="3">
										Heading 3
									</DropdownMenuRadioItem>
									<DropdownMenuRadioItem value="4">
										Heading 4
									</DropdownMenuRadioItem>
									<DropdownMenuRadioItem value="5">
										Heading 5
									</DropdownMenuRadioItem>
									<DropdownMenuRadioItem value="6">
										Heading 6
									</DropdownMenuRadioItem>
								</DropdownMenuRadioGroup>
							</DropdownMenuContent>
						</DropdownMenu>
						<TooltipContent>
							<div className="flex items-center gap-1">
								<span>Heading</span>
								<Kbd>Ctrl</Kbd>
								<Kbd>Alt</Kbd>
								<Kbd>H</Kbd>
							</div>
						</TooltipContent>
					</Tooltip>

					<Tooltip>
						<TooltipTrigger asChild>
							<Button
								className={cn(
									"size-8",
									editorInstance.isActive("code") &&
										"bg-primary text-primary-foreground"
								)}
								disabled={disabled}
								onClick={toggleCode}
								size="icon"
								variant="ghost"
							>
								<Code className="size-4" />
							</Button>
						</TooltipTrigger>
						<TooltipContent>
							<div className="flex items-center gap-1">
								<span>Inline Code</span>
								<Kbd>Ctrl</Kbd>
								<Kbd>`</Kbd>
							</div>
						</TooltipContent>
					</Tooltip>

					<Tooltip>
						<TooltipTrigger asChild>
							<Button
								className={cn(
									"size-8",
									editorInstance.isActive("codeBlock") &&
										"bg-primary text-primary-foreground"
								)}
								disabled={disabled}
								onClick={toggleCodeBlock}
								size="icon"
								variant="ghost"
							>
								<Code className="size-4" />
							</Button>
						</TooltipTrigger>
						<TooltipContent>
							<div className="flex items-center gap-1">
								<span>Code Block</span>
								<Kbd>Ctrl</Kbd>
								<Kbd>Alt</Kbd>
								<Kbd>C</Kbd>
							</div>
						</TooltipContent>
					</Tooltip>

					<div className="mx-1 h-4 w-px bg-border" />

					<Tooltip>
						<TooltipTrigger asChild>
							<Button
								className={cn(
									editorInstance.isActive("bulletList") &&
										"bg-primary text-primary-foreground"
								)}
								disabled={disabled}
								onClick={toggleBulletList}
								size="icon"
								variant="ghost"
							>
								<List className="size-4" />
							</Button>
						</TooltipTrigger>
						<TooltipContent>
							<div className="flex items-center gap-1">
								<span>Bullet List</span>
								<Kbd>Ctrl</Kbd>
								<Kbd>Shift</Kbd>
								<Kbd>B</Kbd>
							</div>
						</TooltipContent>
					</Tooltip>

					<Tooltip>
						<TooltipTrigger asChild>
							<Button
								className={cn(
									editorInstance.isActive("orderedList") &&
										"bg-primary text-primary-foreground"
								)}
								disabled={disabled}
								onClick={toggleOrderedList}
								size="icon"
								variant="ghost"
							>
								<ListOrdered className="size-4" />
							</Button>
						</TooltipTrigger>
						<TooltipContent>
							<div className="flex items-center gap-1">
								<span>Ordered List</span>
								<Kbd>Ctrl</Kbd>
								<Kbd>Shift</Kbd>
								<Kbd>O</Kbd>
							</div>
						</TooltipContent>
					</Tooltip>

					<Tooltip>
						<Dialog onOpenChange={setLinkDialogOpen} open={linkDialogOpen}>
							<TooltipTrigger asChild>
								<Button
									className={cn(
										"size-8",
										editorInstance.isActive("link") &&
											"bg-primary text-primary-foreground"
									)}
									disabled={disabled}
									onClick={() => setLinkDialogOpen(true)}
									size="icon"
									variant="ghost"
								>
									<LinkIcon className="size-4" />
								</Button>
							</TooltipTrigger>
							<DialogContent>
								<DialogHeader>
									<DialogTitle>Add Link</DialogTitle>
									<DialogDescription>
										Enter the URL you want to link to
									</DialogDescription>
								</DialogHeader>
								<div className="flex flex-col gap-4">
									<Input
										autoFocus
										onChange={(e) => setLinkUrl(e.target.value)}
										onKeyDown={(e) => {
											if (e.key === "Enter") {
												handleLinkSubmit();
											}
										}}
										placeholder="https://example.com"
										value={linkUrl}
									/>
									<div className="flex justify-end gap-2">
										<Button
											onClick={() => setLinkDialogOpen(false)}
											variant="outline"
										>
											Cancel
										</Button>
										<Button onClick={handleLinkSubmit}>Add Link</Button>
									</div>
								</div>
							</DialogContent>
						</Dialog>
						<TooltipContent>
							<div className="flex items-center gap-2">
								<span>Link</span>
								<Kbd>Ctrl</Kbd>

								<Kbd>K</Kbd>
							</div>
						</TooltipContent>
					</Tooltip>

					<Tooltip>
						<Dialog onOpenChange={setImageDialogOpen} open={imageDialogOpen}>
							<TooltipTrigger asChild>
								<Button
									className={cn(
										"size-8",
										editorInstance.isActive("image") &&
											"bg-primary text-primary-foreground"
									)}
									disabled={disabled}
									onClick={() => setImageDialogOpen(true)}
									size="icon"
									variant="ghost"
								>
									<ImageIcon className="size-4" />
								</Button>
							</TooltipTrigger>
							<DialogContent>
								<DialogHeader>
									<DialogTitle>Add Image</DialogTitle>
									<DialogDescription>
										Enter the image URL and optional alt text
									</DialogDescription>
								</DialogHeader>
								<div className="flex flex-col gap-4">
									<Input
										autoFocus
										onChange={(e) => setImageUrl(e.target.value)}
										onKeyDown={(e) => {
											if (e.key === "Enter") {
												handleImageSubmit();
											}
										}}
										placeholder="https://example.com/image.jpg"
										value={imageUrl}
									/>
									<Input
										onChange={(e) => setImageAlt(e.target.value)}
										onKeyDown={(e) => {
											if (e.key === "Enter") {
												handleImageSubmit();
											}
										}}
										placeholder="Alt text (optional)"
										value={imageAlt}
									/>
									<div className="flex justify-end gap-2">
										<Button
											onClick={() => setImageDialogOpen(false)}
											variant="outline"
										>
											Cancel
										</Button>
										<Button onClick={handleImageSubmit}>Add Image</Button>
									</div>
								</div>
							</DialogContent>
						</Dialog>
						<TooltipContent>
							<div className="flex items-center gap-2">
								<span>Image</span>
							</div>
						</TooltipContent>
					</Tooltip>

					<Tooltip>
						<Popover onOpenChange={setEmojiPickerOpen} open={emojiPickerOpen}>
							<PopoverTrigger asChild>
								<TooltipTrigger asChild>
									<Button
										className="size-8"
										disabled={disabled}
										size="icon"
										variant="ghost"
									>
										<Smile className="size-4" />
									</Button>
								</TooltipTrigger>
							</PopoverTrigger>
							<PopoverContent className="w-auto border-0 p-0">
								<EmojiPicker
									onEmojiSelect={(emoji) => {
										editorInstance
											.chain()
											.focus("end")
											.insertContent(emoji.emoji)
											.run();
										setEmojiPickerOpen(false);
									}}
								/>
							</PopoverContent>
						</Popover>
						<TooltipContent>
							<div className="flex items-center gap-2">
								<span>Emoji</span>
							</div>
						</TooltipContent>
					</Tooltip>

					<Tooltip>
						<Popover onOpenChange={setGifPickerOpen} open={gifPickerOpen}>
							<PopoverTrigger asChild>
								<TooltipTrigger asChild>
									<Button
										className="size-8"
										disabled={disabled}
										size="icon"
										variant="ghost"
									>
										<span className="size-4 font-bold text-xs">GIF</span>
									</Button>
								</TooltipTrigger>
							</PopoverTrigger>
							<PopoverContent className="w-auto border-0 p-0">
								<GifPicker
									gifs={gifs}
									onGifSelect={(gif) => {
										const directUrl = convertToDirectImageUrl(gif.url);
										editorInstance
											.chain()
											.focus()
											.setImage({
												src: directUrl,
												alt: gif.title,
												width: IMAGE_EMBEDDING_MAX_WIDTH,
											})
											.run();
										setGifPickerOpen(false);
									}}
								/>
							</PopoverContent>
						</Popover>
						<TooltipContent>
							<div className="flex items-center gap-2">
								<span>GIF</span>
							</div>
						</TooltipContent>
					</Tooltip>

					{onAttachClick && (
						<Tooltip>
							<TooltipTrigger asChild>
								<Button
									className="size-8"
									disabled={disabled || isUploading}
									onClick={onAttachClick}
									size="icon"
									variant="ghost"
								>
									{isUploading ? (
										<Loader2 className="size-4 animate-spin" />
									) : (
										<Paperclip className="size-4" />
									)}
								</Button>
							</TooltipTrigger>
							<TooltipContent>
								<div className="flex items-center gap-2">
									<span>Attach files</span>
								</div>
							</TooltipContent>
						</Tooltip>
					)}

					<div className="mx-1 h-4 w-px bg-border" />

					<Tooltip>
						<TooltipTrigger asChild>
							<Button
								className="size-8"
								disabled={disabled}
								onClick={undo}
								size="icon"
								variant="ghost"
							>
								<Undo2 className="size-4" />
							</Button>
						</TooltipTrigger>
						<TooltipContent>
							<div className="flex items-center gap-2">
								<span>Undo</span>
								<Kbd>Ctrl</Kbd>

								<Kbd>Z</Kbd>
							</div>
						</TooltipContent>
					</Tooltip>

					<Tooltip>
						<TooltipTrigger asChild>
							<Button
								className="size-8"
								disabled={disabled}
								onClick={redo}
								size="icon"
								variant="ghost"
							>
								<Redo2 className="size-4" />
							</Button>
						</TooltipTrigger>
						<TooltipContent>
							<div className="flex items-center gap-2">
								<span>Redo</span>
								<Kbd>Ctrl</Kbd>

								<Kbd>Shift</Kbd>

								<Kbd>Z</Kbd>
							</div>
						</TooltipContent>
					</Tooltip>
				</div>
			</TooltipProvider>

			<div
				className={cn(
					"max-w-none overflow-y-hidden py-2 pl-3",
					editorClassName
				)}
			>
				<EditorContent className={tiptapEditorStyles} editor={editorInstance} />
			</div>
			<div className="absolute right-3.5 bottom-2.5 flex items-center gap-2">
				{onCancel && (
					<TooltipProvider>
						<Tooltip>
							<TooltipTrigger asChild>
								<Button onClick={onCancel} size="sm" variant="ghost">
									Cancel
								</Button>
							</TooltipTrigger>
							<TooltipContent>
								<div className="flex items-center gap-1">
									<span>Cancel</span>
									<Kbd>Esc</Kbd>
								</div>
							</TooltipContent>
						</Tooltip>
					</TooltipProvider>
				)}
				<TooltipProvider>
					<Tooltip>
						<TooltipTrigger asChild>
							<Button
								disabled={disabled || !onSubmit}
								onClick={handleSubmit}
								size="sm"
								variant="outline"
							>
								{submitLabel}
								<Send className="size-4" />
							</Button>
						</TooltipTrigger>
						<TooltipContent>
							<div className="flex items-center gap-1">
								<span>Send</span>
								{sendKeyMode === "cmd-enter" ? (
									<>
										<Kbd>Cmd</Kbd>
										<Kbd>Enter</Kbd>
									</>
								) : (
									<Kbd>Enter</Kbd>
								)}
							</div>
						</TooltipContent>
					</Tooltip>
				</TooltipProvider>
			</div>
		</div>
	);
}
